import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { memo } from 'react';

import Anchor from 'components/Anchor';
import TextWithRichContent from 'components/TextWithRichContent';

import { sendEvent } from 'lib/gtag';
import toLastCommentText from 'lib/htmlParsers/parseLastCommentsHtml';
import { getOfferImageLink } from 'lib/image';
import { toArticleLink, toOfferLink, toThreadLink } from 'lib/links';

import LastCommentsListItemImage from './ListItemImage';
import LastCommentsListItemTitle from './ListItemTitle';
import LastCommentsListItemUser from './ListItemUser';

const GA_EVENT_CATEGORY = 'last_comments_cards';

const getHref = ({
  offerSlug,
  articleSlug,
  threadSlug,
  scrollToComment = null,
}) =>
  (offerSlug && toOfferLink(offerSlug, { scrollToComment })) ||
  (articleSlug && toArticleLink(articleSlug, { scrollToComment })) ||
  (threadSlug && toThreadLink(threadSlug, { scrollToComment }));

const getImage = ({ offerPhoto, articlePhoto }) => {
  if (offerPhoto) {
    return getOfferImageLink(offerPhoto, 120);
  }

  return articlePhoto || null;
};

const onImageClick = () =>
  sendEvent({ category: GA_EVENT_CATEGORY, action: 'click_image' });

const onTextClick = () =>
  sendEvent({ category: GA_EVENT_CATEGORY, action: 'click_text' });

const LastCommentsListItem = ({
  article = null,
  commentDate,
  commentId,
  commentText,
  offer = null,
  thread = null,
  userName,
  userPhoto = null,
  userTypeName,
  userUsername,
}) => {
  const hrefOptions = {
    offerSlug: offer?.offerSlug,
    articleSlug: article?.articleSlug,
    threadSlug: thread?.threadSlug,
  };
  const href = getHref(hrefOptions);
  const hrefWithCommentIdHash = getHref({
    ...hrefOptions,
    scrollToComment: commentId,
  });

  /**
   * Ideally this shouldn't exist, but there were a case where users could send
   * comments to offers before being associated to an actual offer on the
   * server. That case were mitigated by removing the possibility of sending
   * comments on this specific case on the frontend. But backend might still
   * be able to accept this req. So, to make sure it won't break that list in
   * an edge case, we keep doing the following.
   */
  if (!thread && !article && !offer) {
    return null;
  }

  return (
    <div className="flex border-b border-neutral-high-300 py-4 dark:border-neutral-low-400">
      <NextLink href={href} prefetch={false} passHref>
        <Anchor className="size-20 md:size-[100px]">
          <LastCommentsListItemImage
            as="a"
            description={
              offer?.offerTitle || article?.articleTitle || thread?.threadTitle
            }
            image={getImage({
              offerPhoto: offer?.offerPhoto,
              articlePhoto: article?.articlePhoto,
            })}
            thread={!!thread}
            onClick={onImageClick}
          />
        </Anchor>
      </NextLink>
      <div className="ml-4 flex w-full flex-col items-start justify-between">
        <LastCommentsListItemTitle
          gaEventCategory={GA_EVENT_CATEGORY}
          href={hrefWithCommentIdHash}
          title={article?.articleTitle || thread?.threadTitle || null}
          titleType={(article && 'ARTICLE') || (thread && 'THREAD') || null}
        />
        <NextLink href={hrefWithCommentIdHash} passHref scroll>
          <TextWithRichContent
            as="a"
            className={
              !commentText.includes(' ') &&
              'max-w-[200px] overflow-hidden text-ellipsis break-normal'
            }
            full
            onClick={onTextClick}
          >
            {toLastCommentText(commentText)}
          </TextWithRichContent>
        </NextLink>
        <LastCommentsListItemUser
          commentDate={commentDate}
          gaEventCategory={GA_EVENT_CATEGORY}
          userName={userName}
          userPhoto={userPhoto}
          userTypeName={userTypeName}
          userUsername={userUsername}
        />
      </div>
    </div>
  );
};

LastCommentsListItem.propTypes = {
  article: PropTypes.shape({
    articleId: PropTypes.number,
    articleTitle: PropTypes.string.isRequired,
    articleSlug: PropTypes.string.isRequired,
  }),
  commentDate: PropTypes.string.isRequired,
  commentText: PropTypes.string.isRequired,
  offer: PropTypes.shape({
    offerId: PropTypes.number,
    offerPhoto: PropTypes.string.isRequired,
    offerSlug: PropTypes.string.isRequired,
  }),
  thread: PropTypes.shape({
    threadId: PropTypes.number,
    threadTitle: PropTypes.string.isRequired,
    threadSlug: PropTypes.string.isRequired,
  }),
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string,
  userTypeName: PropTypes.string.isRequired,
  userUsername: PropTypes.string.isRequired,
};

export default memo(LastCommentsListItem);
